/**
 * Handover acknowledgement — one operation, one person, one batch.
 *
 * A person taking over an operation (a new operator, a supervisor, a replacement mid-run) is
 * refused their first action on it (428 HANDOVER_ACK_REQUIRED) until they have read the
 * operation's handover note and e-signed it. The note is the record's; who signed is the
 * batch's. Same pattern as the general-instructions acknowledgement, scoped to the operation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Modal, Spin, Tag, Typography } from 'antd';
import { BookOpenCheck, Handshake } from 'lucide-react';
import { toast } from 'react-toastify';
import ESignAuth from '@/components/ESignAuth';
import { ackApi } from '@/utils/ackApi';

const { Text } = Typography;

export default function AcknowledgeHandoverModal({ open, batchNumber, operationId, onAcknowledged, onCancel, stationCode }) {
  const [loading, setLoading] = useState(false);
  const [block, setBlock] = useState(null);
  const [esignOpen, setEsignOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!batchNumber || !operationId) return;
    setLoading(true);
    const res = await ackApi.operationHandover(batchNumber, operationId);
    setBlock(res?.status === 'success' ? res.data : null);
    setLoading(false);
  }, [batchNumber, operationId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const sign = async ({ username, password, comment }) => {
    setSaving(true);
    const res = await ackApi.acknowledgeHandover(batchNumber, operationId, { username, password, comment }, stationCode);
    setSaving(false);
    setEsignOpen(false);
    if (res?.status === 'success') {
      toast.success(`Handover for ${block?.name || 'the operation'} acknowledged on ${batchNumber}`);
      onAcknowledged?.(res.data);
    } else {
      toast.error(res?.message || 'The acknowledgement could not be recorded');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={640}
      title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <Handshake size={18} /> Handover — {block?.name || 'operation'} · {batchNumber}
        {block?.code && <Tag>{block.code}</Tag>}
      </span>}
      footer={[
        <Button key="c" onClick={onCancel}>Not now</Button>,
        <Button key="s" type="primary" icon={<BookOpenCheck size={14} />} disabled={!block || block.acknowledged} loading={saving} onClick={() => setEsignOpen(true)}>
          I have read this — sign
        </Button>,
      ]}
    >
      {loading ? <Spin /> : !block ? (
        <Alert type="error" showIcon message="The handover note could not be loaded." />
      ) : (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
                 message="You are picking up this operation. Read what the person before you leaves you, then sign."
                 description="Every operator or supervisor signs once per operation per batch — a replacement mid-run included." />
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{block.text}</div>
          <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>
            hash {String(block.hash || '').slice(0, 12)}{block.acknowledged ? ' · already acknowledged by you' : ''}
          </Text>
        </>
      )}
      <ESignAuth
        visible={esignOpen}
        onCancel={() => setEsignOpen(false)}
        onSubmit={sign}
        req={{ batch_number: batchNumber, action_type: 'Read and understood the handover for this operation', esign_type: 'basic' }}
      />
    </Modal>
  );
}

AcknowledgeHandoverModal.propTypes = {
  open: PropTypes.bool,
  batchNumber: PropTypes.string,
  operationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAcknowledged: PropTypes.func,
  onCancel: PropTypes.func,
  stationCode: PropTypes.string,
};
